import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import Loading from '@carbon/react/es/components/Loading/Loading';
import Button from '@carbon/react/es/components/Button/Button';
import Toggle from '@carbon/react/es/components/Toggle/Toggle';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import TrashCan from '@carbon/icons-react/es/TrashCan';
import Location from '@carbon/icons-react/es/Location';
import Add from '@carbon/icons-react/es/Add';
import {
  OrgRole,
  roleAtLeast,
  type CreateOperationalZoneInput,
  type Defender,
  type OperationalAsset,
  type OperationalPicture,
  type OperationalZone,
  type Threat,
  type ThreatLevel,
} from '@ddp/shared';

import { PageHeader } from '../components/PageHeader.js';
import { useAuthStore } from '../auth/store.js';
import { useI18n, type MessageKey } from '../i18n/index.js';
import {
  useOperationalPicture,
  useOperationalZones,
} from '../features/operations/use-operational-picture.js';
import {
  attachDefender,
  fetchDefenderCandidates,
  removeDefender,
  updateDefender,
} from '../features/operations/defender-api.js';
import { createZone, updateZone } from '../features/operations/zone-api.js';
import { useEstimatedCenter, type LatLon } from '../features/operations/use-estimated-center.js';

// Carte chargée à la demande : MapLibre est lourd et reste hors du bundle de la coquille (NFR-11).
const OpsMap = lazy(() => import('../features/operations/OpsMap.js'));

/** Couleurs partagées avec les couches de la carte (OpsMap) — pastilles de légende et de liste. */
const LEVEL_COLOR: Record<ThreatLevel, string> = {
  LOW: '#08bdba',
  MEDIUM: '#f1c21b',
  HIGH: '#ff832b',
  CRITICAL: '#da1e28',
};
const STATUS_COLOR: Record<Defender['status'], string> = {
  AVAILABLE: '#24a148',
  ENGAGED: '#4589ff',
  RETURNING: '#8a3ffc',
  CHARGING: '#f1c21b',
  OFFLINE: '#6f6f6f',
};
const DEFENDER_STATUSES = Object.keys(STATUS_COLOR) as Defender['status'][];
/** Ordre de gravité décroissant : la menace la plus grave remonte en tête de liste. */
const LEVEL_RANK: Record<ThreatLevel, number> = { CRITICAL: 3, HIGH: 2, MEDIUM: 1, LOW: 0 };

/** Actions de gestion transmises aux lignes défenseurs ; absentes (⇒ lecture seule) pour un VIEWER. */
interface DefenderActions {
  busy: boolean;
  placingId: string | null;
  onStatus: (defenderId: string, status: Defender['status']) => void;
  onReposition: (defenderId: string) => void;
  onRemove: (defenderId: string) => void;
}

function Dot({ color }: { color: string }) {
  return <span className="ddp-ops__dot" style={{ backgroundColor: color }} aria-hidden="true" />;
}

function ThreatRow({ threat }: { threat: Threat }) {
  const { t } = useI18n();
  const fields: string[] = [];
  if (threat.speedMps !== null)
    fields.push(`${t('ops.field.speed')} ${Math.round(threat.speedMps)} m/s`);
  if (threat.headingDeg !== null)
    fields.push(`${t('ops.field.heading')} ${Math.round(threat.headingDeg)}°`);
  if (threat.altitudeM !== null)
    fields.push(`${t('ops.field.altitude')} ${Math.round(threat.altitudeM)} m`);
  if (threat.sizeM !== null) fields.push(`${t('ops.field.size')} ${threat.sizeM.toFixed(1)} m`);
  return (
    <li className="ddp-ops__item">
      <Dot color={LEVEL_COLOR[threat.level]} />
      <div className="ddp-ops__item-body">
        <span className="ddp-ops__item-title">
          {t(`ops.class.${threat.class}` as MessageKey)} ·{' '}
          {t(`ops.level.${threat.level}` as MessageKey)}
        </span>
        <span className="ddp-ops__item-meta">{fields.join(' · ')}</span>
      </div>
    </li>
  );
}

function DefenderRow({
  defender,
  actions,
}: {
  defender: Defender;
  actions: DefenderActions | null;
}) {
  const { t } = useI18n();
  const fields: string[] = [];
  if (defender.batteryPct !== null)
    fields.push(`${t('ops.field.battery')} ${defender.batteryPct}%`);
  if (defender.lat !== null && defender.lon !== null)
    fields.push(`${defender.lat.toFixed(4)}, ${defender.lon.toFixed(4)}`);
  const placing = actions?.placingId === defender.id;
  return (
    <li className={`ddp-ops__item${placing ? ' ddp-ops__item--placing' : ''}`}>
      <Dot color={STATUS_COLOR[defender.status]} />
      <div className="ddp-ops__item-body">
        <span className="ddp-ops__item-title">
          {defender.assetName} · {t(`ops.status.${defender.status}` as MessageKey)}
        </span>
        {fields.length > 0 && <span className="ddp-ops__item-meta">{fields.join(' · ')}</span>}

        {actions && (
          <div className="ddp-ops__item-actions">
            <Select
              id={`def-status-${defender.id}`}
              size="sm"
              hideLabel
              labelText={t('ops.manage.status')}
              value={defender.status}
              disabled={actions.busy}
              onChange={(e) => actions.onStatus(defender.id, e.target.value as Defender['status'])}
            >
              {DEFENDER_STATUSES.map((s) => (
                <SelectItem key={s} value={s} text={t(`ops.status.${s}` as MessageKey)} />
              ))}
            </Select>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={t('ops.manage.reposition')}
              renderIcon={Location}
              disabled={actions.busy}
              isSelected={placing}
              onClick={() => actions.onReposition(defender.id)}
            />
            <Button
              kind="danger--ghost"
              size="sm"
              hasIconOnly
              iconDescription={t('ops.manage.remove')}
              renderIcon={TrashCan}
              disabled={actions.busy}
              onClick={() => actions.onRemove(defender.id)}
            />
          </div>
        )}
      </div>
    </li>
  );
}

/** Rattachement d'un nouveau défenseur depuis la flotte (EDITOR+). Recharge la liste des candidats. */
function AddDefender({
  defenderCount,
  busy,
  onAttach,
}: {
  defenderCount: number;
  busy: boolean;
  onAttach: (assetId: string) => void;
}) {
  const { t } = useI18n();
  const [candidates, setCandidates] = useState<OperationalAsset[]>([]);
  const [selected, setSelected] = useState<string>('');

  // Recharge quand le nombre de défenseurs change : un retrait rend le matériel à nouveau candidat.
  useEffect(() => {
    let active = true;
    fetchDefenderCandidates()
      .then((list) => {
        if (!active) return;
        setCandidates(list);
        setSelected((cur) => (list.some((a) => a.id === cur) ? cur : (list[0]?.id ?? '')));
      })
      .catch(() => {
        if (active) setCandidates([]);
      });
    return () => {
      active = false;
    };
  }, [defenderCount]);

  if (candidates.length === 0) {
    return <p className="ddp-ops__empty">{t('ops.manage.noCandidates')}</p>;
  }
  return (
    <div className="ddp-ops__add">
      <Select
        id="ops-add-defender"
        size="sm"
        hideLabel
        labelText={t('ops.manage.assetLabel')}
        value={selected}
        disabled={busy}
        onChange={(e) => setSelected(e.target.value)}
      >
        {candidates.map((asset) => (
          <SelectItem key={asset.id} value={asset.id} text={asset.name} />
        ))}
      </Select>
      <Button
        kind="tertiary"
        size="sm"
        renderIcon={Add}
        disabled={busy || !selected}
        onClick={() => selected && onAttach(selected)}
      >
        {t('ops.manage.add')}
      </Button>
    </div>
  );
}

function SidePanel({
  picture,
  actions,
  onAttach,
}: {
  picture: OperationalPicture;
  actions: DefenderActions | null;
  onAttach: (assetId: string) => void;
}) {
  const { t } = useI18n();
  const threats = useMemo(
    () => [...picture.threats].sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level]),
    [picture.threats],
  );
  return (
    <aside className="ddp-ops__panel">
      <section className="ddp-ops__group">
        <h2 className="ddp-ops__group-title">
          {t('ops.threats.title')} <span className="ddp-ops__count">{threats.length}</span>
        </h2>
        {threats.length === 0 ? (
          <p className="ddp-ops__empty">{t('ops.threats.none')}</p>
        ) : (
          <ul className="ddp-ops__list">
            {threats.map((threat) => (
              <ThreatRow key={threat.id} threat={threat} />
            ))}
          </ul>
        )}
      </section>

      <section className="ddp-ops__group">
        <h2 className="ddp-ops__group-title">
          {t('ops.defenders.title')}{' '}
          <span className="ddp-ops__count">{picture.defenders.length}</span>
        </h2>
        {picture.defenders.length === 0 ? (
          <p className="ddp-ops__empty">{t('ops.defenders.none')}</p>
        ) : (
          <ul className="ddp-ops__list">
            {picture.defenders.map((defender) => (
              <DefenderRow key={defender.id} defender={defender} actions={actions} />
            ))}
          </ul>
        )}
        {/* Rattachement réservé EDITOR+ : présent seulement quand les actions le sont. */}
        {actions && (
          <AddDefender
            defenderCount={picture.defenders.length}
            busy={actions.busy}
            onAttach={onAttach}
          />
        )}
      </section>

      <section className="ddp-ops__group">
        <h2 className="ddp-ops__group-title">{t('ops.legend.title')}</h2>
        <ul className="ddp-ops__legend">
          {(Object.keys(LEVEL_COLOR) as ThreatLevel[]).map((level) => (
            <li key={level} className="ddp-ops__legend-item">
              <Dot color={LEVEL_COLOR[level]} />
              {t(`ops.level.${level}` as MessageKey)}
            </li>
          ))}
        </ul>
      </section>
    </aside>
  );
}

/** Création d'une zone défendue (EDITOR+) : nom + centre (lat/lon) + rayon. */
function ZoneCreateForm({
  busy,
  defaultCenter,
  onCreate,
  onCancel,
}: {
  busy: boolean;
  /** Centre estimé (position navigateur, repli profil) pré-rempli tant que l'utilisateur n'a rien saisi. */
  defaultCenter: LatLon | null;
  onCreate: (input: CreateOperationalZoneInput) => void;
  onCancel?: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [radius, setRadius] = useState('3000');

  // Pré-remplissage du centre par la position estimée — uniquement si le champ est encore vide
  // (on ne piétine jamais une saisie manuelle).
  useEffect(() => {
    if (!defaultCenter) return;
    setLat((cur) => (cur === '' ? defaultCenter.lat.toFixed(5) : cur));
    setLon((cur) => (cur === '' ? defaultCenter.lon.toFixed(5) : cur));
  }, [defaultCenter]);

  const latN = Number(lat);
  const lonN = Number(lon);
  const radiusN = Number(radius);
  const valid =
    name.trim().length >= 2 &&
    lat.trim() !== '' &&
    lon.trim() !== '' &&
    Number.isFinite(latN) &&
    latN >= -90 &&
    latN <= 90 &&
    Number.isFinite(lonN) &&
    lonN >= -180 &&
    lonN <= 180 &&
    Number.isFinite(radiusN) &&
    radiusN > 0;

  const submit = () => {
    if (valid && !busy)
      onCreate({ name: name.trim(), centerLat: latN, centerLon: lonN, radiusM: radiusN });
  };

  return (
    <form
      className="ddp-ops__zone-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <h2 className="ddp-ops__group-title">{t('ops.zone.create.title')}</h2>
      <div className="ddp-ops__zone-fields">
        <TextInput
          id="zone-name"
          labelText={t('ops.zone.create.name')}
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
        />
        <TextInput
          id="zone-lat"
          type="number"
          labelText={t('ops.zone.create.lat')}
          value={lat}
          disabled={busy}
          onChange={(e) => setLat(e.target.value)}
        />
        <TextInput
          id="zone-lon"
          type="number"
          labelText={t('ops.zone.create.lon')}
          value={lon}
          disabled={busy}
          onChange={(e) => setLon(e.target.value)}
        />
        <TextInput
          id="zone-radius"
          type="number"
          labelText={t('ops.zone.create.radius')}
          value={radius}
          disabled={busy}
          onChange={(e) => setRadius(e.target.value)}
        />
      </div>
      <div className="ddp-ops__zone-actions">
        <Button type="submit" size="sm" disabled={!valid || busy}>
          {t('ops.zone.create.submit')}
        </Button>
        {onCancel && (
          <Button kind="ghost" size="sm" disabled={busy} onClick={onCancel}>
            {t('ops.zone.create.cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}

/**
 * Vue opérationnelle counter-UAS (ADR-0040, C2/C3/C5) : carte de situation + listes menaces/défenseurs,
 * **gestion de la flotte défensive** et **des zones** (création, armement de la simulation) pour les
 * rôles EDITOR+. L'autorisation reste portée par `OrgRole` côté API ; le persona ne fait que proposer
 * cette vue (ADR-0031). Un VIEWER voit tout, en lecture seule.
 */
export function OpsPage() {
  const { t } = useI18n();
  const role = useAuthStore((s) => s.currentUser?.memberships[0]?.role);
  const canManage = role !== undefined && roleAtLeast(role, OrgRole.EDITOR);

  // Centre estimé pour pré-remplir la création de zone (ADR-0040, C6) : position navigateur, repli
  // sur la localisation approximative du profil.
  const approxLat = useAuthStore((s) => s.currentUser?.approxLat ?? null);
  const approxLon = useAuthStore((s) => s.currentUser?.approxLon ?? null);
  const profileCenter: LatLon | null =
    approxLat !== null && approxLon !== null ? { lat: approxLat, lon: approxLon } : null;
  const estimatedCenter = useEstimatedCenter(profileCenter);

  const {
    zones,
    loading: zonesLoading,
    error: zonesError,
    reload: reloadZones,
  } = useOperationalZones();
  const [zoneId, setZoneId] = useState<string | null>(null);

  // Sélection par défaut : la première zone dès qu'elles sont chargées.
  useEffect(() => {
    if (zoneId === null && zones.length > 0) setZoneId(zones[0]!.id);
  }, [zones, zoneId]);

  const { picture, loading, error, refresh } = useOperationalPicture(zoneId);
  const basemapConfigured = Boolean(import.meta.env.VITE_OPS_BASEMAP_URL);

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Défenseur en cours de positionnement : le prochain clic sur la carte lui pose une position.
  const [placingId, setPlacingId] = useState<string | null>(null);
  // Formulaire de création de zone déplié (quand des zones existent déjà).
  const [showCreateZone, setShowCreateZone] = useState(false);

  const currentZone: OperationalZone | undefined = zones.find((z) => z.id === zoneId);

  /** Enveloppe commune aux mutations : occupe l'IHM, rafraîchit la situation, remonte l'erreur. */
  const run = useCallback(
    async (op: () => Promise<unknown>) => {
      setBusy(true);
      setActionError(null);
      try {
        await op();
        refresh();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : t('ops.manage.error'));
      } finally {
        setBusy(false);
      }
    },
    [refresh, t],
  );

  const handleAttach = useCallback(
    (assetId: string) => {
      if (zoneId) void run(() => attachDefender(zoneId, { assetId }));
    },
    [zoneId, run],
  );
  const handleStatus = useCallback(
    (defenderId: string, status: Defender['status']) =>
      void run(() => updateDefender(defenderId, { status })),
    [run],
  );
  const handleRemove = useCallback(
    (defenderId: string) => {
      if (placingId === defenderId) setPlacingId(null);
      void run(() => removeDefender(defenderId));
    },
    [run, placingId],
  );
  const handleReposition = useCallback(
    (defenderId: string) => setPlacingId((cur) => (cur === defenderId ? null : defenderId)),
    [],
  );
  const handlePick = useCallback(
    (lon: number, lat: number) => {
      const target = placingId;
      if (!target) return;
      setPlacingId(null);
      void run(() => updateDefender(target, { lat, lon }));
    },
    [placingId, run],
  );
  const handleCreateZone = useCallback(
    (input: CreateOperationalZoneInput) =>
      void run(async () => {
        const zone = await createZone(input);
        reloadZones();
        setZoneId(zone.id);
        setShowCreateZone(false);
      }),
    [run, reloadZones],
  );
  const handleToggleSim = useCallback(
    (zone: OperationalZone) =>
      void run(async () => {
        await updateZone(zone.id, { simulationEnabled: !zone.simulationEnabled });
        reloadZones();
      }),
    [run, reloadZones],
  );

  const actions: DefenderActions | null = canManage
    ? {
        busy,
        placingId,
        onStatus: handleStatus,
        onReposition: handleReposition,
        onRemove: handleRemove,
      }
    : null;

  const placingName = placingId
    ? (picture?.defenders.find((d) => d.id === placingId)?.assetName ?? '')
    : '';

  return (
    <section className="ddp-page__section ddp-ops">
      <PageHeader title={t('ops.title')} lead={t('ops.subtitle')}>
        {picture && (
          <span className="ddp-ops__live">
            <span className="ddp-ops__live-dot" aria-hidden="true" />
            {t('ops.live')} · {t('ops.updated')}{' '}
            {new Date(picture.generatedAt).toLocaleTimeString()}
          </span>
        )}
      </PageHeader>

      {!zonesLoading &&
        zones.length === 0 &&
        (canManage ? (
          <ZoneCreateForm busy={busy} defaultCenter={estimatedCenter} onCreate={handleCreateZone} />
        ) : (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title={t('ops.zone.none')}
            subtitle={t('ops.zone.empty')}
          />
        ))}

      {zones.length > 0 && (
        <div className="ddp-ops__controls">
          <Select
            id="ops-zone"
            labelText={t('ops.zone.label')}
            value={zoneId ?? ''}
            onChange={(e) => setZoneId(e.target.value)}
          >
            {zones.map((zone) => (
              <SelectItem key={zone.id} value={zone.id} text={zone.name} />
            ))}
          </Select>
          {canManage && currentZone && (
            <Toggle
              id="ops-sim"
              size="sm"
              labelText={t('ops.sim.label')}
              labelA={t('ops.sim.off')}
              labelB={t('ops.sim.on')}
              toggled={currentZone.simulationEnabled}
              disabled={busy}
              onToggle={() => handleToggleSim(currentZone)}
            />
          )}
          {canManage && (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Add}
              disabled={busy}
              onClick={() => setShowCreateZone((v) => !v)}
            >
              {t('ops.zone.new')}
            </Button>
          )}
        </div>
      )}

      {zones.length > 0 && canManage && showCreateZone && (
        <ZoneCreateForm
          busy={busy}
          defaultCenter={estimatedCenter}
          onCreate={handleCreateZone}
          onCancel={() => setShowCreateZone(false)}
        />
      )}

      {(zonesError ?? error ?? actionError) && (
        <InlineNotification
          kind="error"
          lowContrast
          onCloseButtonClick={() => setActionError(null)}
          title={t('ops.picture.error')}
          subtitle={zonesError ?? error ?? actionError ?? ''}
        />
      )}

      {!basemapConfigured && zones.length > 0 && (
        <p className="ddp-ops__hint">{t('ops.basemap.missing')}</p>
      )}

      {placingId && (
        <div className="ddp-ops__placing" role="status">
          <span className="ddp-ops__live-dot" aria-hidden="true" />
          <span>
            {t('ops.manage.placing')}
            {placingName && <strong> {placingName}</strong>}
          </span>
          <Button kind="ghost" size="sm" onClick={() => setPlacingId(null)}>
            {t('ops.manage.cancelPlacing')}
          </Button>
        </div>
      )}

      {zones.length > 0 && (
        <div className="ddp-ops__layout">
          <div className="ddp-ops__map-slot">
            {loading && !picture && (
              <div className="ddp-ops__loading">
                <Loading withOverlay={false} small description={t('ops.picture.loading')} />
              </div>
            )}
            {picture && (
              <Suspense
                fallback={
                  <div className="ddp-ops__loading">
                    <Loading withOverlay={false} small description={t('ops.picture.loading')} />
                  </div>
                }
              >
                {/* `key` = zone : on recrée proprement la carte au changement de zone. */}
                <OpsMap
                  key={zoneId}
                  picture={picture}
                  picking={placingId !== null}
                  onPick={handlePick}
                />
              </Suspense>
            )}
          </div>
          {picture && <SidePanel picture={picture} actions={actions} onAttach={handleAttach} />}
        </div>
      )}
    </section>
  );
}
